/**
 * Generic apiFetch wrapper — ported from
 * /home/svc/exante/black-mirror/full-sales-ui/src/services/api.ts lines 1-100.
 *
 * Adaptations vs the black-mirror source:
 *   - StorageKey import uses the Sales Coach namespace (D-15 — value
 *     `sc_dashboard_auth_token`) instead of black-mirror's namespace.
 *   - API_BASE_URL reads VITE_APP_API_URL env; empty string for dev because
 *     the vite proxy in vite.config.ts forwards /api to the dashboard backend
 *     on port 3004.
 *   - 401 hook: calls the module-local `onUnauthorized` handler, registered
 *     via `setUnauthorizedHandler` from `lib/query-client.ts`. This lets the
 *     SPA clear auth state and redirect to /login without passing React refs
 *     through every fetch site (D-30 QueryClient 401 handler).
 *
 * Black-mirror's domain-specific endpoint wrappers (Person, Call, CallAnalysis,
 * AiCosts, etc.) are NOT ported — Sales Coach adds its own typed wrappers in
 * services/dashboard-api.ts (D-12).
 */

import { StorageKey } from '@/constants/storageKeys';

// NOTE (milestone v1.4): this app calls `/salescoach/api/*` endpoints, not `/api/*`.
// `apiFetch` itself is path-agnostic — callers pass `/salescoach/api/overview/kpis` etc.
// The dev proxy in vite.config.ts forwards both `/salescoach/api` and `/auth` to the
// dashboard backend (127.0.0.1:3004). In production the same path rewrites
// happen at nginx, so API_BASE_URL stays empty.
const API_BASE_URL = import.meta.env.VITE_APP_API_URL || '';

/** Thrown on non-2xx API responses; includes HTTP status + raw detail. */
export class ApiError extends Error {
  readonly status: number;

  readonly detail: unknown;

  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

/** Singleton 401 handler — registered by App root via query-client.ts. */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

function getAuthToken(): string | null {
  try {
    return localStorage.getItem(StorageKey.AUTH_TOKEN);
  } catch (error) {
    // localStorage can throw in privacy mode / sandboxed contexts.
    console.error('Could not read from localStorage:', error);
    return null;
  }
}

export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getAuthToken();

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Do not set Content-Type for FormData — browser sets multipart boundary.
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30_000);

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      signal: options.signal ?? controller.signal,
    });
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(
        'Request timeout - the server took too long to respond',
        0,
      );
    }
    throw error;
  }
  clearTimeout(timeoutId);

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({} as Record<string, unknown>));
    const detail = (errorData as { detail?: unknown }).detail;
    let message: string;
    if (typeof detail === 'string') {
      message = detail;
    } else if (
      detail &&
      typeof detail === 'object' &&
      typeof (detail as { message?: unknown }).message === 'string'
    ) {
      message = (detail as { message: string }).message;
    } else if (typeof (errorData as { error?: unknown }).error === 'string') {
      message = (errorData as { error: string }).error;
    } else {
      message = `HTTP error! status: ${response.status}`;
    }

    // D-30 — 401 triggers the global unauthorized handler (clear auth + redirect).
    if (response.status === 401 && onUnauthorized) {
      try {
        onUnauthorized();
      } catch {
        // Swallow handler errors — they must not mask the ApiError below.
      }
    }

    throw new ApiError(message, response.status, detail ?? errorData);
  }

  // 204 No Content — no body to parse.
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}
